import React, { useState, useEffect, useMemo } from 'react';
import { customersApi, proxmoxApi } from '../services/api';
import toast from 'react-hot-toast';
import './Proxmox.css';

const API_BASE = '/api/v1';

const getToken = () => localStorage.getItem('token');

const apiFetch = async (path: string) => {
  const resp = await fetch(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!resp.ok) throw new Error(`API error: ${resp.status}`);
  return resp.json();
};

interface PVENode {
  name: string;
  status: string;
  cpu_usage: number;
  memory_used: number;
  memory_total: number;
  uptime: number;
}

interface PVEBilling {
  vcpu_qty: number;
  vmem_qty: number;
  ssd_qty: number;
  vcpu_price: string | null;
  vmem_price: string | null;
  ssd_price: string | null;
  billing_cycle: string | null;
  monthly_total: number;
}

interface PVEVM {
  vmid: number;
  name: string;
  node: string;
  vm_type: string;
  status: string;
  cpus: number;
  memory_allocated_gb: number;
  disk_allocated_gb: number;
  cpu_usage: number;
  memory_used_gb: number;
  uptime: number;
  netin: number;
  netout: number;
  matched_customer_id: number | null;
  matched_customer_name: string | null;
  billing?: PVEBilling | null;
  gone?: boolean;            // mapped VM Proxmox no longer has -> red, nothing billed
}

interface Customer {
  id: number;
  xero_name: string;
  [key: string]: any;
}

function formatUptime(seconds: number): string {
  if (!seconds) return '-';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  if (d > 0) return `${d}d ${h}h`;
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let val = bytes;
  while (val >= 1024 && i < units.length - 1) { val /= 1024; i++; }
  return `${val.toFixed(1)} ${units[i]}`;
}

type SortKey = keyof PVEVM;

function Proxmox() {
  const [status, setStatus] = useState<any>(null);
  const [nodes, setNodes] = useState<PVENode[]>([]);
  const [vms, setVMs] = useState<PVEVM[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filterNode, setFilterNode] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMapping, setFilterMapping] = useState<'all' | 'mapped' | 'unmapped'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('vmid');
  const [sortAsc, setSortAsc] = useState(true);
  const [defaultRates, setDefaultRates] = useState<{ vcpu: string; vmem: string; ssd: string } | null>(null);

  // Mapping modal state
  const [mappingVM, setMappingVM] = useState<PVEVM | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [vcpuPrice, setVcpuPrice] = useState('');
  const [vmemPrice, setVmemPrice] = useState('');
  const [ssdPrice, setSsdPrice] = useState('');
  const [mappingLoading, setMappingLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [s, n, v] = await Promise.all([
        apiFetch('/proxmox/status'),
        apiFetch('/proxmox/nodes'),
        apiFetch('/proxmox/vms'),
      ]);
      setStatus(s);
      setDefaultRates(s?.default_rates ?? null);
      setNodes(n);
      setVMs(v);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const loadVMs = async () => {
    try {
      const v = await apiFetch('/proxmox/vms?refresh=true');
      setVMs(v);
    } catch (e: any) {
      toast.error('Failed to refresh VMs');
    }
  };

  const openMappingModal = async (vm: PVEVM) => {
    setMappingVM(vm);
    setCustomerSearch('');
    setSelectedCustomerId(vm.matched_customer_id);
    setVcpuPrice(vm.billing?.vcpu_price ?? defaultRates?.vcpu ?? '');
    setVmemPrice(vm.billing?.vmem_price ?? defaultRates?.vmem ?? '');
    setSsdPrice(vm.billing?.ssd_price ?? defaultRates?.ssd ?? '');
    if (customers.length === 0) {
      try {
        const custs = await customersApi.list();
        setCustomers(custs);
      } catch {
        toast.error('Failed to load customers');
      }
    }
  };

  const handleMap = async () => {
    if (!mappingVM || !selectedCustomerId) return;
    setMappingLoading(true);
    try {
      await proxmoxApi.mapVM(String(mappingVM.vmid), selectedCustomerId, {
        vmName: mappingVM.name,
        cpus: mappingVM.cpus,
        memoryGb: mappingVM.memory_allocated_gb,
        diskGb: mappingVM.disk_allocated_gb,
        vcpuPrice,
        vmemPrice,
        ssdPrice,
        billingCycle: 'monthly',
      });
      toast.success('VM mapped successfully');
      setMappingVM(null);
      await loadVMs();
    } catch {
      toast.error('Failed to map VM');
    } finally {
      setMappingLoading(false);
    }
  };

  const handleUnmap = async (vm: PVEVM) => {
    setMappingLoading(true);
    try {
      await proxmoxApi.unmapVM(String(vm.vmid));
      toast.success('VM unmapped');
      await loadVMs();
    } catch {
      toast.error('Failed to unmap VM');
    } finally {
      setMappingLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await proxmoxApi.sync();
      toast.success(`Sync complete: ${result.matched} matched, ${result.created_mappings} new mappings`);
      await loadVMs();
    } catch {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  };

  const filteredCustomers = useMemo(() => {
    if (!customerSearch) return customers;
    const q = customerSearch.toLowerCase();
    return customers.filter(c => c.xero_name?.toLowerCase().includes(q));
  }, [customers, customerSearch]);

  const filteredVMs = useMemo(() => {
    let result = [...vms];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(v => v.name.toLowerCase().includes(q) || String(v.vmid).includes(q));
    }
    if (filterNode) result = result.filter(v => v.node === filterNode);
    if (filterType) result = result.filter(v => v.vm_type === filterType);
    if (filterStatus) result = result.filter(v => v.status === filterStatus);
    if (filterMapping === 'mapped') result = result.filter(v => v.matched_customer_id !== null);
    if (filterMapping === 'unmapped') result = result.filter(v => v.matched_customer_id === null);
    result.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') return sortAsc ? av - bv : bv - av;
      return sortAsc ? String(av ?? '').localeCompare(String(bv ?? '')) : String(bv ?? '').localeCompare(String(av ?? ''));
    });
    return result;
  }, [vms, search, filterNode, filterType, filterStatus, filterMapping, sortKey, sortAsc]);

  const mappedCount = useMemo(() => vms.filter(v => v.matched_customer_id !== null).length, [vms]);

  // Billable quantities for the VM in the mapping modal (rounded to whole units, matching the backend)
  const modalQty = useMemo(() => {
    if (!mappingVM) return { vcpu: 0, vmem: 0, ssd: 0 };
    return {
      vcpu: mappingVM.cpus,
      vmem: Math.round(mappingVM.memory_allocated_gb),
      ssd: Math.round(mappingVM.disk_allocated_gb),
    };
  }, [mappingVM]);

  const modalTotal = useMemo(() => (
    modalQty.vcpu * (parseFloat(vcpuPrice) || 0)
    + modalQty.vmem * (parseFloat(vmemPrice) || 0)
    + modalQty.ssd * (parseFloat(ssdPrice) || 0)
  ), [modalQty, vcpuPrice, vmemPrice, ssdPrice]);

  const SortHeader = ({ label, field }: { label: string; field: SortKey }) => (
    <th onClick={() => handleSort(field)} className="pve-sortable">
      {label} {sortKey === field ? (sortAsc ? '▲' : '▼') : ''}
    </th>
  );

  if (loading) return <div className="pve-page"><div className="pve-loading">Loading Proxmox data...</div></div>;

  return (
    <div className="pve-page">
      <div className="pve-header">
        <h1>Proxmox VE</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="pve-refresh-btn" onClick={handleSync} disabled={syncing}>
            {syncing ? '⟳ Syncing...' : '🔗 Auto-Sync'}
          </button>
          <button className="pve-refresh-btn" onClick={loadData}>↻ Refresh</button>
        </div>
      </div>

      {error && <div className="pve-error">{error}</div>}

      {/* Status Card */}
      {status && (
        <div className={`pve-status-card ${status.connected ? 'connected' : 'disconnected'}`}>
          <div className="pve-status-indicator" />
          <div>
            <strong>{status.connected ? 'Connected' : 'Disconnected'}</strong>
            {status.version && <span className="pve-version">Proxmox VE {status.version}</span>}
            {status.error && <span className="pve-error-text">{status.error}</span>}
          </div>
        </div>
      )}



      {/* VM Table */}
      <div className="pve-vm-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>Virtual Machines & Containers ({filteredVMs.length})</h2>
          <div className="pve-filter-buttons">
            <button className={filterMapping === 'all' ? 'active' : ''} onClick={() => setFilterMapping('all')}>All ({vms.length})</button>
            <button className={filterMapping === 'mapped' ? 'active' : ''} onClick={() => setFilterMapping('mapped')}>Mapped ({mappedCount})</button>
            <button className={filterMapping === 'unmapped' ? 'active' : ''} onClick={() => setFilterMapping('unmapped')}>Unmapped ({vms.length - mappedCount})</button>
          </div>
        </div>
        <div className="pve-filters">
          <input
            type="text" placeholder="Search by name or ID..." value={search}
            onChange={e => setSearch(e.target.value)} className="pve-search"
          />
          <select value={filterNode} onChange={e => setFilterNode(e.target.value)} className="pve-filter-select">
            <option value="">All Nodes</option>
            {nodes.map(n => <option key={n.name} value={n.name}>{n.name}</option>)}
          </select>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} className="pve-filter-select">
            <option value="">All Types</option>
            <option value="qemu">QEMU</option>
            <option value="lxc">LXC</option>
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="pve-filter-select">
            <option value="">All Status</option>
            <option value="running">Running</option>
            <option value="stopped">Stopped</option>
          </select>
        </div>
        <div className="pve-table-wrapper">
          <table className="pve-table">
            <thead>
              <tr>
                <SortHeader label="VM ID" field="vmid" />
                <SortHeader label="Name" field="name" />
                <th>Matched Customer</th>
                <th>Billing /mo</th>
                <SortHeader label="Node" field="node" />
                <SortHeader label="Type" field="vm_type" />
                <SortHeader label="Status" field="status" />
                <SortHeader label="CPUs" field="cpus" />
                <SortHeader label="Memory (GB)" field="memory_allocated_gb" />
                <SortHeader label="Storage (GB)" field="disk_allocated_gb" />
                <SortHeader label="CPU Usage" field="cpu_usage" />
                <th>Mem Usage</th>
                <SortHeader label="Uptime" field="uptime" />
                <th>Net I/O</th>
              </tr>
            </thead>
            <tbody>
              {filteredVMs.length === 0 ? (
                <tr><td colSpan={14} className="pve-empty">No VMs found. Token permissions may need to be expanded.</td></tr>
              ) : (
                filteredVMs.map(v => (
                  <tr key={`${v.node}-${v.vm_type}-${v.vmid}`} className={v.gone ? 'gone' : ''}
                      title={v.gone ? 'This mapped VM is no longer in Proxmox - nothing is billed, its lines are off in Invoice Prep' : undefined}>
                    <td>{v.vmid}</td>
                    <td className="pve-vm-name">{v.name || '-'}{v.gone && <span className="pve-tag danger" title="Not in Proxmox any more">gone · not billed</span>}</td>
                    <td>
                      {v.matched_customer_id ? (
                        <div className="pve-customer-cell">
                          <span className="pve-match-badge mapped">{v.matched_customer_name}</span>
                          <button className="pve-map-btn change" onClick={() => openMappingModal(v)}>Change</button>
                          <button className="pve-map-btn unmap" onClick={() => handleUnmap(v)}>✕</button>
                        </div>
                      ) : (
                        <button className="pve-map-btn map" onClick={() => openMappingModal(v)}>Map</button>
                      )}
                    </td>
                    <td className="pve-billing-cell">
                      {v.billing ? (
                        v.billing.monthly_total > 0 ? (
                          <span
                            className="pve-billing-total-badge"
                            title={`vCPU ${v.billing.vcpu_qty} × R${v.billing.vcpu_price ?? '—'}  ·  vMem ${v.billing.vmem_qty}GB × R${v.billing.vmem_price ?? '—'}  ·  SSD ${v.billing.ssd_qty}GB × R${v.billing.ssd_price ?? '—'}`}
                          >
                            R {v.billing.monthly_total.toFixed(2)}
                          </span>
                        ) : (
                          <button className="pve-map-btn change" onClick={() => openMappingModal(v)}>Set price</button>
                        )
                      ) : (
                        <span className="pve-billing-na">–</span>
                      )}
                    </td>
                    <td>{v.node}</td>
                    <td><span className={`pve-badge pve-badge-type-${v.vm_type}`}>{v.vm_type.toUpperCase()}</span></td>
                    <td><span className={`pve-badge pve-badge-${v.status}`}>{v.status}</span></td>
                    <td>{v.cpus}</td>
                    <td>{v.memory_allocated_gb.toFixed(1)}</td>
                    <td>{v.disk_allocated_gb.toFixed(1)}</td>
                    <td>
                      <div className="pve-cpu-bar">
                        <div className="pve-cpu-fill" style={{ width: `${Math.min(v.cpu_usage, 100)}%` }} />
                        <span>{v.cpu_usage.toFixed(1)}%</span>
                      </div>
                    </td>
                    <td>{v.memory_used_gb.toFixed(1)} / {v.memory_allocated_gb.toFixed(1)}</td>
                    <td>{formatUptime(v.uptime)}</td>
                    <td className="pve-netio">{formatBytes(v.netin)} / {formatBytes(v.netout)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mapping Modal */}
      {mappingVM && (
        <div className="pve-mapping-modal-overlay" onClick={() => setMappingVM(null)}>
          <div className="pve-mapping-modal" onClick={e => e.stopPropagation()}>
            <div className="pve-modal-header">
              <h3>Map VM to Customer</h3>
              <button className="pve-modal-close" onClick={() => setMappingVM(null)}>✕</button>
            </div>
            <div className="pve-modal-body">
              <div className="pve-modal-vm-info">
                <strong>{mappingVM.name}</strong>
                <span className="pve-modal-vmid">ID: {mappingVM.vmid} · {mappingVM.node} · {mappingVM.vm_type.toUpperCase()}</span>
              </div>
              <input
                type="text"
                placeholder="Search customers..."
                value={customerSearch}
                onChange={e => setCustomerSearch(e.target.value)}
                className="pve-customer-search"
                autoFocus
              />
              <div className="pve-customer-list">
                {filteredCustomers.map(c => (
                  <div
                    key={c.id}
                    className={`pve-customer-option ${selectedCustomerId === c.id ? 'selected' : ''}`}
                    onClick={() => setSelectedCustomerId(c.id)}
                  >
                    {c.xero_name}
                  </div>
                ))}
                {filteredCustomers.length === 0 && (
                  <div className="pve-customer-empty">No customers found</div>
                )}
              </div>

              <div className="pve-billing-section">
                <div className="pve-billing-heading">Billable resources <span>(R / unit / month)</span></div>
                <div className="pve-billing-row">
                  <span className="pve-billing-label">vCPU</span>
                  <span className="pve-billing-qty">{modalQty.vcpu} cores</span>
                  <span className="pve-billing-x">×</span>
                  <div className="pve-billing-price">
                    <span className="pve-billing-rand">R</span>
                    <input type="number" min="0" step="0.01" placeholder="0.00"
                      value={vcpuPrice} onChange={e => setVcpuPrice(e.target.value)} />
                  </div>
                </div>
                <div className="pve-billing-row">
                  <span className="pve-billing-label">vMem</span>
                  <span className="pve-billing-qty">{modalQty.vmem} GB</span>
                  <span className="pve-billing-x">×</span>
                  <div className="pve-billing-price">
                    <span className="pve-billing-rand">R</span>
                    <input type="number" min="0" step="0.01" placeholder="0.00"
                      value={vmemPrice} onChange={e => setVmemPrice(e.target.value)} />
                  </div>
                </div>
                <div className="pve-billing-row">
                  <span className="pve-billing-label">SSD</span>
                  <span className="pve-billing-qty">{modalQty.ssd} GB</span>
                  <span className="pve-billing-x">×</span>
                  <div className="pve-billing-price">
                    <span className="pve-billing-rand">R</span>
                    <input type="number" min="0" step="0.01" placeholder="0.00"
                      value={ssdPrice} onChange={e => setSsdPrice(e.target.value)} />
                  </div>
                </div>
                <div className="pve-billing-total-row">
                  <span>Total / month</span>
                  <strong>R {modalTotal.toFixed(2)}</strong>
                </div>
              </div>
            </div>
            <div className="pve-modal-footer">
              <button className="pve-modal-cancel" onClick={() => setMappingVM(null)}>Cancel</button>
              <button
                className="pve-modal-map"
                onClick={handleMap}
                disabled={!selectedCustomerId || mappingLoading}
              >
                {mappingLoading ? 'Mapping...' : 'Map'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Proxmox;
